/**
 * Types pour les zones de prospection
 * Zones dessinées sur la carte pour définir des périmètres de prospection
 */

// Couleurs disponibles pour les zones
export type ZoneColor = 
  | 'red'
  | 'blue'
  | 'green'
  | 'yellow'
  | 'purple'
  | 'orange'
  | 'pink'
  | 'cyan'
  | 'teal'
  | 'indigo'
  | 'emerald'
  | 'slate';

// Labels pour les couleurs
export const ZONE_COLOR_LABELS: Record<ZoneColor, string> = {
  red: 'Rouge',
  blue: 'Bleu',
  green: 'Vert',
  yellow: 'Jaune',
  purple: 'Violet',
  orange: 'Orange',
  pink: 'Rose',
  cyan: 'Cyan',
  teal: 'Turquoise',
  indigo: 'Indigo',
  emerald: 'Émeraude',
  slate: 'Ardoise'
};

// Valeurs hexadécimales pour les couleurs
export const ZONE_COLOR_HEX: Record<ZoneColor, string> = {
  red: '#ef4444',
  blue: '#3b82f6',
  green: '#22c55e',
  yellow: '#eab308',
  purple: '#a855f7',
  orange: '#f97316',
  pink: '#ec4899',
  cyan: '#06b6d4',
  teal: '#14b8a6',
  indigo: '#6366f1',
  emerald: '#10b981',
  slate: '#64748b'
};

// Coordonnées [longitude, latitude]
export type Coordinate = [number, number];

// Zone de prospection (format base de données)
export interface ProspectionZone {
  id: string;
  agencyId: string;
  userId: string;
  userName?: string;
  name: string;
  description?: string;
  color: ZoneColor;
  coordinates: Coordinate[]; // Polygone: minimum 3 points
  isActive: boolean;
  isVisible: boolean; // Visibilité sur la carte
  createdAt: string;
  updatedAt: string;
}

// Format pour créer une nouvelle zone
export interface CreateProspectionZoneData {
  name: string;
  description?: string;
  color: ZoneColor;
  coordinates: Coordinate[];
  isActive?: boolean;
  isVisible?: boolean;
  createdAt?: string; // Date de création personnalisée (format ISO 8601)
}

// Format pour mettre à jour une zone
export interface UpdateProspectionZoneData {
  name?: string;
  description?: string;
  color?: ZoneColor;
  coordinates?: Coordinate[];
  isActive?: boolean;
  isVisible?: boolean;
}

// Zone en cours de dessin (avant sauvegarde)
export interface DrawingZone {
  coordinates: Coordinate[];
  color: ZoneColor;
  isComplete: boolean; // true quand le polygone est fermé
}

// Paramètres de filtre pour récupérer les zones
export interface ProspectionZoneFilters {
  agencyId?: string;
  activeOnly?: boolean;
}

// Statistiques d'une zone
export interface ZoneStats {
  zoneId: string;
  markerCount: number; // Nombre de markers dans la zone
  contactCount: number; // Nombre de contacts dans la zone
}

// Mode de dessin de la carte
export type DrawMode = 'none' | 'drawing' | 'editing';

// État du gestionnaire de zones
export interface ZoneManagerState {
  zones: ProspectionZone[];
  selectedZoneId: string | null;
  drawMode: DrawMode;
  drawingZone: DrawingZone | null;
  isLoading: boolean;
  error: string | null;
}
